// A3 (2026-08-30) — texting lives above the page, like calling already does.
//
// Mark's original ask was that every phone number in the CRM be actionable.
// COMMS-LOG-8 delivered half of it: `PhoneLink` made numbers dialable from
// anywhere, because `CallContext` owns the Device above the router and any
// screen can ask it to place a call.
//
// Messaging had no such owner. `ComposeSheet` was mounted inside
// `ConversationsPanel`, so the only way to text somebody was to go to Comms
// first and search for a person whose number you were already looking at.
// That is the friction Mark named at the start of all this: "we want ease for
// users so they dont try outside crm". A broker on a deal page who has to
// navigate away to send one text will use their own phone instead — and that
// message is then invisible to the firm, unlogged, and lost when they leave.
//
// So this mirrors CallProvider exactly: mounted above the router in App.js,
// owning the sheet, exposed as one function. Panels become consumers.
//
// Null-safe by the same rule as `useCall`: returns null outside the provider
// (the client portal has no comms), so callers feature-detect rather than
// crash.

import { createContext, useCallback, useContext, useMemo, useState } from "react";

import ComposeSheet from "@/components/admin/comms/ComposeSheet";

const ComposeContext = createContext(null);

export function useCompose() {
  return useContext(ComposeContext);
}

export function ComposeProvider({ children }) {
  // `null` when closed. Holding the whole request in one piece of state means
  // the sheet can never be open with a stale recipient from the last one.
  const [request, setRequest] = useState(null);

  const openCompose = useCallback((mode, { number, name } = {}) => {
    if (!number) return;
    setRequest({ mode: mode === "whatsapp" ? "whatsapp" : "sms", number, name: name || "" });
  }, []);

  const closeCompose = useCallback(() => setRequest(null), []);

  const value = useMemo(() => ({ openCompose, closeCompose, open: !!request }),
    [openCompose, closeCompose, request]);

  return (
    <ComposeContext.Provider value={value}>
      {children}
      {/* Rendered here rather than by each caller, so a deal page does not
          need to know that a sheet exists. `canSend` is deliberately both
          channels: the SERVER owns whether a given number can carry one and
          whether WhatsApp's 24-hour window is open, and it says so in a
          message worth reading. Guessing here would mean hiding a button that
          would have worked, which is worse than an honest refusal. */}
      <ComposeSheet
        open={!!request}
        mode={request?.mode || "sms"}
        canSend={["sms", "whatsapp"]}
        prefill={request ? { number: request.number, name: request.name } : null}
        onClose={closeCompose}
        onSent={closeCompose}
      />
    </ComposeContext.Provider>
  );
}
